import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, DollarSign, UserPlus, Star, Trash2, Key, Building2, Edit } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import axios from "axios";

const AdminDashboard = () => {
  // Stats State
  const [stats, setStats] = useState({
    total: 0,
    showcased: 0,
    pending: 0,
    newThisMonth: 0
  });

  // User Management State
  const [users, setUsers] = useState([]);
  const [partners, setPartners] = useState([]); // Store all partners
  const [loadingUsers, setLoadingUsers] = useState(true);
  
  // Dialog States
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isResetOpen, setIsResetOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  
  // Form States
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUser, setNewUser] = useState({ email: "", password: "", role: "partner" });
  const [resetPass, setResetPass] = useState("");
  const [editForm, setEditForm] = useState({ role: "", partner_id: "none" });

  useEffect(() => {
    fetchStatsAndPartners();
    fetchUsers();
  }, []);

  const fetchStatsAndPartners = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/partners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const partnersList = res.data;
      setPartners(partnersList); // Store full list
      setStats({
        total: partnersList.length,
        showcased: partnersList.filter(p => p.showcase).length,
        pending: partnersList.filter(p => !p.approved).length,
        newThisMonth: 5 // Mock or calc
      });
    } catch (e) {
      console.error(e);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm("Are you sure? This will also delete any linked partner profile.")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers();
      fetchStatsAndPartners(); // Refresh partners too
    } catch (e) {
      alert("Failed to delete user");
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewUser({ email: "", password: "", role: "partner" });
      setIsCreateOpen(false);
      fetchUsers();
      alert("User created successfully");
    } catch (e) {
      alert("Failed to create user. Email may exist.");
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${selectedUser.id}/reset-password`, {
        new_password: resetPass
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert("Password updated");
      setResetPass("");
      setSelectedUser(null);
      setIsResetOpen(false);
    } catch (e) {
      alert("Failed to reset password");
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${selectedUser.id}/role`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert("User updated");
      setIsEditOpen(false);
      fetchUsers();
      fetchStatsAndPartners(); // Refresh links
    } catch (e) {
      alert("Failed to update user");
    }
  };

  // Helper to find partner for a user
  const getPartnerForUser = (userId) => {
    return partners.find(p => p.owner_user_id === userId);
  };

  const openEditDialog = (user) => {
    const partner = getPartnerForUser(user.id);
    setSelectedUser(user);
    setEditForm({
      role: user.roles[0],
      partner_id: partner ? partner.id : "none"
    });
    setIsEditOpen(true);
  };

  return (
    <div className="space-y-8 pb-10">
      <h1 className="text-3xl font-bold">Admin Dashboard</h1>
      
      {/* 1. Admin Center Matrix (Stats) */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Partners</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Showcased</CardTitle>
            <Star className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.showcased}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">New Signups</CardTitle>
            <UserPlus className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">+{stats.newThisMonth}</div>
            <p className="text-xs text-muted-foreground">Past 30 days</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Est. Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">$124,500</div>
            <p className="text-xs text-muted-foreground">+15% from last month</p>
          </CardContent>
        </Card>
      </div>

      {/* 2. User Management Section (Merged) */}
      <div className="space-y-4">
        <div className="flex justify-between items-center border-b pb-4">
          <div>
             <h2 className="text-2xl font-bold">User Administration</h2>
             <p className="text-gray-500">Manage system access, create admins, and reset passwords.</p>
          </div>
          
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button><UserPlus className="mr-2 h-4 w-4" /> Add New User</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Email Address</Label>
                  <Input type="email" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} required placeholder="user@example.com" />
                </div>
                <div className="space-y-2">
                  <Label>Initial Password</Label>
                  <Input type="password" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={newUser.role} onValueChange={v => setNewUser({...newUser, role: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="partner">Partner</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full">Create User</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardContent className="p-0">
            {loadingUsers ? (
              <div className="p-8 text-center text-gray-500">Loading users...</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Partner / Company</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map(user => {
                    const partner = getPartnerForUser(user.id);
                    return (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.email}</TableCell>
                        <TableCell>
                          {partner ? (
                            <div className="flex items-center gap-2 text-blue-700">
                                <Building2 className="h-3 w-3" />
                                <span className="font-bold">{partner.name}</span>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs italic">No Partner Profile</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={user.roles[0] === 'admin' ? "default" : "secondary"}>
                            {user.roles[0].toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={user.is_active ? "bg-green-100 text-green-800 hover:bg-green-100" : "bg-red-100 text-red-800"}>
                            {user.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          {/* EDIT ROLE / LINK Dialog */}
                          <Dialog open={selectedUser?.id === user.id && isEditOpen} onOpenChange={(open) => !open && setIsEditOpen(false)}>
                            <DialogTrigger asChild>
                              <Button variant="ghost" size="icon" onClick={() => openEditDialog(user)}>
                                <Edit className="h-4 w-4 text-blue-600" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Edit User: {user.email}</DialogTitle>
                              </DialogHeader>
                              <form onSubmit={handleEditUser} className="space-y-4 pt-4">
                                <div className="space-y-2">
                                  <Label>Role</Label>
                                  <Select value={editForm.role} onValueChange={v => setEditForm({...editForm, role: v})}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="partner">Partner</SelectItem>
                                      <SelectItem value="admin">Admin</SelectItem>
                                      <SelectItem value="viewer">Viewer</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-2">
                                  <Label>Link to Partner Profile</Label>
                                  <Select value={editForm.partner_id} onValueChange={v => setEditForm({...editForm, partner_id: v})}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="none">No Linked Partner</SelectItem>
                                      {partners.map(p => (
                                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <p className="text-xs text-gray-500">Assign this user as the owner of a partner profile.</p>
                                </div>
                                <Button type="submit" className="w-full">Save Changes</Button>
                              </form>
                            </DialogContent>
                          </Dialog>

                          {/* RESET PASSWORD Dialog */}
                          <Dialog open={selectedUser?.id === user.id && isResetOpen} onOpenChange={(open) => !open && setIsResetOpen(false)}>
                            <DialogTrigger asChild>
                              <Button variant="ghost" size="icon" onClick={() => { setSelectedUser(user); setIsResetOpen(true); }}>
                                <Key className="h-4 w-4 text-gray-500" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Reset Password</DialogTitle>
                              </DialogHeader>
                              <form onSubmit={handleResetPassword} className="space-y-4 pt-4">
                                <div className="space-y-2">
                                    <Label>New Password</Label>
                                    <Input 
                                      type="password" 
                                      value={resetPass} 
                                      onChange={e => setResetPass(e.target.value)} 
                                      required 
                                    />
                                </div>
                                <Button type="submit" className="w-full">Update Password</Button>
                              </form>
                            </DialogContent>
                          </Dialog>
                          
                          <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteUser(user.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
